// extended by react-360-view
import { Box, Grid } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { Component } from 'react';
import HotspotButton from '../hotspot/hotspot-buttons';
import { connect } from 'react-redux';
import { carHotSpotCardStatusSet } from '../../store/interior-slice';
import theme from '../../config/theme';
import Buttons from '../buttons/buttons';

import {
  car360LoadedSet,
  carHotSpotCardOpenToggled,
  carHotSpotEnableSet,
  carHotSpotPositionsSet,
  carImageRatioSet,
  closeHotSpotPositionSet,
  currentCarHotSpotIndexSet,
  dragToExploreVisibilitySet,
  exteriorZoomScaleSet,
  rotate360VisibilitySet,
} from '../../store/car-slice';
import { Loading } from '../../components';
import { isMobile } from 'react-device-detect';
import ExteriorButtonContainer from '../buttons/exterior-button-container';
import { urlParam } from '../../utils/url-param';
import DragToExplore from '../../pages/vdp/drag-to-explore';

const Canvas = styled('canvas')(() => ({
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'contain',
  height: '100%',
  position: 'relative',
  width: '100%',
}));

const V360 = styled(Grid)(({ page }) => ({
  alignItems: 'center',
  backgroundColor: 'white',
  display: 'flex',
  justifyContent: 'center',
  left: '50%',
  position: page === 'car' ? 'absolute' : 'initial',
  top: '50%',
  transform: page === 'car' ? 'translate(-50%, -50%)' : 'none',
}));

const viewPage = urlParam('viewPage');

class React360Viewer extends Component {
  constructor() {
    super();
    this.viewPortElementRef = React.createRef();
    this.canvas = React.createRef();
    this.ctx = null;
    this.isMobile = false;
    this.currentImage = null;
    this.currentLeftPosition = this.currentTopPosition = 0;
    this.currentCanvasImage = null;
    this.movementStart = 0;
    this.movement = false;
    this.activeImage = 0;
    this.timeOfLastMove = 0;
    this.xOfLastMove = -1;
    this.lastJumpFrame = 0;
    this.lastVelocity = 0;
    this.state = {
      images: [],
      firstImageURL: null,
      currentImageOrder: 0,
      multipleLoadedImage: 0, // multiple of loaded image used for loading image Queue
      currentLoop: 0,
      currentScale: 1,
      canvasLoaded: false,
      isFullScreen: false,
      loopTimeoutId: 0,
      playing: false,
      velocity: 0, // speed of transition
      startX: null,
      startTimestamp: 0,
      inactivityTimer: null,
      isRotate360ViewedOnce: false,
      frameCounter: 0,
    };
  }

  componentDidMount() {
    this.fetchData();
    document.addEventListener('keydown', this.handleAction);
    if (isMobile) return;
    this.viewPortElementRef.current.style.cursor =
      'url(/pointer.svg) 16 16, grab';
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.playing !== prevState.playing) {
      if (!this.state.playing) {
        this.stop();
      } else {
        this.play();
      }
    }
  }

  componentWillUnmount() {
    document.body.style.cursor = 'default';
    document.removeEventListener('keydown', this.handleAction);
    this.stopMoving();
    this.touchEnd();

    // TODO:
    // this.viewPortElementRef.removeEventListener('wheel', this.handlePinch);
    // this.viewPortElementRef.removeEventListener('pinchstart', this.handlePinch);
    // this.viewPortElementRef.removeEventListener('pinchend', this.handlePinch);
  }

  renderFirstRoundImages() {
    const firstImage = this.props.car?.exterior360Image[0];

    this.state.images.push(firstImage);

    this.props.car?.exterior360Image?.forEach((imageData, index) => {
      if (firstImage?.order === imageData?.order) return;

      // check divisibility to load images
      const currentIndex = index + 1;
      if (currentIndex % 4 === 0) {
        this.state.images.push(imageData);
      }
    });
  }

  checkDivisiblity(index) {
    const isDivisible = (index, divisor) => index % divisor === 0;

    switch (this.state.multipleLoadedImage) {
      case 4:
        return isDivisible(index, 4);
      case 2:
        return index % 4 !== 0 && isDivisible(index, 2);
      case 1:
        return index % 4 !== 0 && index % 2 !== 0 && isDivisible(index, 1);
      default:
        return false;
    }
  }

  renderRestImages() {
    // load images by divisibility of number 4, 2, 1

    const firstImage = this.props.car?.exterior360Image[0];

    this.props.car?.exterior360Image?.forEach((imageData, index) => {
      // first image is already exist in this list so skip first image
      if (firstImage?.order === imageData?.order) return;

      // check divisibility to load images
      if (this.checkDivisiblity(index + 1)) {
        this.state.images.push(imageData);
      }
    });
  }

  fetchData() {
    //  return when loaded complete

    if (this.state.images.length >= this.props.car?.exterior360Image.length)
      return;

    // load first image at first render
    const firstImage = this.props.car?.exterior360Image[0];

    this.setState({ firstImageURL: firstImage?.url });

    if (this.state.multipleLoadedImage === 0) {
      this.renderFirstRoundImages();
      this.setState({ currentImageOrder: firstImage?.order });
    } else {
      this.renderRestImages();
      // sort images after each pushing new image items
      this.state.images.sort((a, b) => a.order - b.order);
    }
    this.preloadImages();

    // set first image order
  }

  preloadImages() {
    if (this.state.images.length > 0) {
      try {
        this.state.images.forEach((model) => {
          this.addImage(model);
        });
      } catch (error) {
        console.error(
          `Something went wrong while loading images: ${error.message}`,
        );
      }
    }
  }

  /**
   * generate image model
   * @param {object} model
   */
  addImage(model) {
    const url = model?.url;
    const order = model?.order;
    const image = new Image();
    image.src = url;
    image.id = order;
    // image.crossOrigin = 'anonymous';
    image.onload = this.onImageLoad.bind(this);
    image.onerror = this.onImageFail.bind(this);
  }

  onImageFail(event) {
    console.error(event);
  }

  /**
   * update current index in images
   * @param {number} currentIndex index
   * @param {object} image img object
   * @param {boolean} zoom zoom set true when click zoom button
   */
  updateSelectedItem(currentIndex, image, zoom) {
    if (currentIndex !== -1) {
      this.setState((prevState) => {
        let updatedList = [...prevState.images];
        const newValue = {
          filename: updatedList[currentIndex]?.filename,
          order: updatedList[currentIndex]?.order,
          url: updatedList[currentIndex]?.url,
          image: image,
          zoom: zoom,
        };
        updatedList[currentIndex] = newValue;
        return { images: updatedList };
      });
    }
  }

  onImageLoad(event) {
    //Find index of images object using findIndex method.
    const currentIndex = this.state.images.findIndex(
      (obj) => obj.order == event.currentTarget.id,
    );

    // push loaded image to the images list by index

    const image = event.currentTarget;
    this.updateSelectedItem(currentIndex, image);

    const firstLoadedImageLength =
      Math.floor(this.props.car?.exterior360Image.length / 4) + 1;

    if (this.state.images.length === firstLoadedImageLength) {
      const { naturalWidth, naturalHeight } = event.target;
      const aspectRatio = naturalWidth / naturalHeight;
      this.setState({ ratio: aspectRatio });
      this.props.carImageRatioSet(aspectRatio);
      this.initData();
      this.setState({ multipleLoadedImage: 4 });
    }
  }

  initData() {
    this.ctx = this?.canvas?.current?.getContext('2d');
    this.checkMobile();
    this.loadInitialImage();
    this.setState({ playing: this.props.autoplay });
  }

  checkMobile() {
    this.isMobile = !!('ontouchstart' in window || navigator.msMaxTouchPoints);
  }

  loadInitialImage() {
    this.currentImage = this.state.images[0].url;
    this.setImage();
  }

  setImage(cached = false) {
    this.currentLeftPosition = this.currentTopPosition = 0;

    if (!cached) {
      this.currentCanvasImage = new Image();
      this.currentCanvasImage.crossOrigin = 'anonymous';
      this.currentCanvasImage.src = this.currentImage;

      this.currentCanvasImage.onload = () => {
        this.setState({ canvasLoaded: true });
        this.props.car360LoadedSet(true);
        const viewportElement =
          this.viewPortElementRef?.current?.getBoundingClientRect();
        if (!this.canvas?.current?.width) return;
        this.canvas.current.width = this.state.isFullScreen
          ? viewportElement.width
          : this.currentCanvasImage.width;
        this.canvas.current.height = this.state.isFullScreen
          ? viewportElement.height
          : this.currentCanvasImage.height;
        this.trackTransforms(this.ctx);
        this.redraw();
      };
      this.currentCanvasImage.onerror = () => {
        console.log('cannot load this image');
      };
    } else {
      this.currentCanvasImage = this.state.images[0].image;
      const viewportElement =
        this.viewPortElementRef?.current?.getBoundingClientRect();
      this.canvas.current.width = this.state.isFullScreen
        ? viewportElement.width
        : this.currentCanvasImage.width;
      this.canvas.current.height = this.state.isFullScreen
        ? viewportElement.height
        : this.currentCanvasImage.height;
      this.trackTransforms(this.ctx);
      this.redraw();
    }
  }

  redraw() {
    try {
      if (this.currentCanvasImage?.id) {
        this.setState({ frameCounter: this.state.frameCounter + 1 });

        if (this.state.frameCounter === 1) {
          this.props.dragToExploreVisibilitySet(false);
        }
      }

      const p1 = this.ctx.transformedPoint(0, 0);
      const p2 = this.ctx.transformedPoint(
        this.canvas?.current.width,
        this.canvas?.current.height,
      );
      const hRatio = this.canvas?.current.width / this.currentCanvasImage.width;
      const vRatio =
        this.canvas?.current.height / this.currentCanvasImage.height;
      const ratio = Math.min(hRatio, vRatio);
      const centerShiftX =
        (this.canvas?.current.width - this.currentCanvasImage.width * ratio) /
        2;
      const centerShiftY =
        (this.canvas?.current.height - this.currentCanvasImage.height * ratio) /
        2;
      this.ctx.clearRect(p1.x, p1.y, p2.x - p1.x, p2.y - p1.y);

      // ? when image was not loaded (just see white screen in canvas)
      // if (!this.currentCanvasImage.complete) {

      // }

      // center image
      this.ctx.drawImage(
        this.currentCanvasImage,
        this.currentLeftPosition,
        this.currentTopPosition,
        this.currentCanvasImage.width,
        this.currentCanvasImage.height,
        centerShiftX,
        centerShiftY,
        this.currentCanvasImage.width * ratio,
        this.currentCanvasImage.height * ratio,
      );
    } catch (e) {
      this.trackTransforms(this.ctx);
    }
  }

  async trackTransforms(ctx) {
    return await new Promise((resolve) => {
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      let xform = svg.createSVGMatrix();
      this.ctx.getTransform = function () {
        return xform;
      };

      const savedTransforms = [];
      const save = ctx.save;
      this.ctx.save = () => {
        savedTransforms.push(xform.translate(0, 0));
        return save.call(this.ctx);
      };
      const restore = ctx.restore;
      this.ctx.restore = () => {
        xform = savedTransforms.pop();
        return restore.call(this.ctx);
      };
      const scale = this.ctx.scale;
      this.ctx.scale = (sx, sy) => {
        xform = xform.scaleNonUniform(sx, sy);
        scale.call(this.ctx, sx, sy);
      };
      const rotate = this.ctx.rotate;
      this.ctx.rotate = (radians) => {
        xform = xform.rotate((radians * 180) / Math.PI);
        rotate.call(this.ctx, radians);
      };
      const translate = this.ctx.translate;
      this.ctx.translate = (dx, dy) => {
        xform = xform.translate(dx, dy);
        translate.call(this.ctx, dx, dy);
      };
      const transform = this.ctx.transform;
      this.ctx.transform = (a, b, c, d, e, f) => {
        const m2 = svg.createSVGMatrix();
        m2.a = a;
        m2.b = b;
        m2.c = c;
        m2.d = d;
        m2.e = e;
        m2.f = f;
        xform = xform.multiply(m2);
        transform.call(this.ctx, a, b, c, d, e, f);
      };
      const setTransform = this.ctx.setTransform;
      this.ctx.setTransform = (a, b, c, d, e, f) => {
        xform.a = a;
        xform.b = b;
        xform.c = c;
        xform.d = d;
        xform.e = e;
        xform.f = f;
        setTransform.call(this.ctx, a, b, c, d, e, f);
      };
      const pt = svg.createSVGPoint();
      this.ctx.transformedPoint = (x, y) => {
        pt.x = x;
        pt.y = y;
        return pt.matrixTransform(xform.inverse());
      };
      resolve(this.ctx);
    });
  }

  turnLeft() {
    this.turnCanvasLeft(1);
  }

  turnRight() {
    this.turnCanvasRight(1);
  }

  turnCanvasRight(itemsSkipped) {
    this.activeImage =
      (this.activeImage + itemsSkipped) % this.state.images.length;

    if (!this.state.images[this.activeImage]?.image) {
      return;
    }

    this.update();
  }

  turnCanvasLeft(itemsSkipped) {
    if (this.activeImage - itemsSkipped < 0) {
      this.activeImage =
        this.state.images.length + (this.activeImage - itemsSkipped);
    } else {
      this.activeImage -= itemsSkipped;
    }

    if (!this.state.images[this.activeImage]?.image) {
      return;
    }

    this.update();
  }

  /**
   * update current image canvas
   */
  update() {
    this.setState({ activeImage: this.activeImage }, () => {
      const selectedImageByActiveImage = this.state.images[this.activeImage];
      const imageOrder = selectedImageByActiveImage?.order;
      const image = selectedImageByActiveImage?.image;

      this.setState({ currentImageOrder: imageOrder });

      this.currentCanvasImage = image;
      this.redraw();
    });
  }

  zoomIn = () => {
    document.body.style.overflow = 'hidden';
    this.zoom(2);
  };

  zoomOut = () => {
    if (this.state.currentScale === 2) document.body.style.overflow = 'hidden';
    // Update the image position
    this.canvas.current.style.left = 0;
    this.canvas.current.style.top = 0;
    this.zoom(1);
  };

  zoom(scale) {
    if (scale > 2 || scale < 1) return;
    this.setState({ currentScale: scale });
    this.props.exteriorZoomScaleSet(scale);

    // update state for hotspot
    setTimeout(() => {
      this.update();
    }, 400);
  }

  onMove(pageX, timeStamp, width) {
    const numberOfFrames = this.state.images?.filter((i) => i?.image)?.length;
    const imageWidth = width;
    let deltaV = (pageX - this.xOfLastMove) / (timeStamp - this.timeOfLastMove);
    this.lastVelocity = deltaV;
    let jumpFrame = Math.min(2, (270 * Math.abs(deltaV)) / imageWidth);

    this.lastJumpFrame = jumpFrame > 0.2 ? jumpFrame : 0;
    if (pageX - this.movementStart > imageWidth / (numberOfFrames * 2)) {
      this.movementStart = pageX;
      for (let index = 0; index < Math.ceil(jumpFrame); index++) {
        setTimeout(() => {
          this.turnCanvasRight(1);
        }, 1);
      }
    } else if (
      pageX - this.movementStart <
      -imageWidth / (numberOfFrames * 2)
    ) {
      this.movementStart = pageX;
      for (let index = 0; index < Math.ceil(jumpFrame); index++) {
        setTimeout(() => {
          this.turnCanvasLeft(1);
        }, 1);
      }
    }
    this.timeOfLastMove = timeStamp;
    this.xOfLastMove = pageX;

    // disable rotate 360 only when is true
    if (this.props.car.rotate360Visibility) {
      this.props.rotate360VisibilitySet(false);
    }
    // show rotate 360 only once
    if (!this.state.isRotate360ViewedOnce) {
      clearTimeout(this.inactivityTimer);

      this.inactivityTimer = setTimeout(() => {
        this.props.rotate360VisibilitySet(true);
        this.setState({ isRotate360ViewedOnce: true });
      }, 3000);
    }
  }

  handleMouseDown = (evt) => {
    this.movement = true;
    this.movementStart = evt.pageX;

    // Store initial position and mouse coordinates
    this.setState({
      mouseX: evt.clientX,
      mouseY: evt.clientY,
    });

    this.setState({ startX: evt.clientX, startTimestamp: evt.timeStamp });

    // EventListener
    this.viewPortElementRef?.current.addEventListener(
      'mousemove',
      this.handleMouseMove,
    );
    this.viewPortElementRef?.current.addEventListener(
      'mouseup',
      this.stopMoving,
    );

    this.generateLoadedItems();
    if (isMobile) return;
    this.viewPortElementRef.current.style.cursor =
      'url(/drag.svg) 16 16, grabbing';
  };

  generateLoadedItems = () => {
    let total = 0;
    this.state.images.forEach((item) => {
      if (item?.image) total++;
    });

    // plus 1 because of index 0 (first image)
    if (
      total ===
      Math.floor(
        this.props.car.exterior360Image.length / this.state.multipleLoadedImage,
      ) +
        1
    ) {
      if (this.state.multipleLoadedImage === 4) {
        this.setState({ multipleLoadedImage: 2 }, () => {
          this.fetchData();
          return;
        });
      }
      if (this.state.multipleLoadedImage === 2) {
        this.setState({ multipleLoadedImage: 1 }, () => {
          this.fetchData();
          return;
        });
      }
    }
  };

  handleTurnCar = (turn) => {
    if (turn === 'right') {
      this.turnCanvasRight(1);
    } else {
      this.turnCanvasLeft(1);
    }
  };

  /**
   * generate transition
   * @param {position} x1
   * @param {timeStamp} t1
   * @param {position} x2
   * @param {timeStamp} t2
   * @returns
   */
  transitionHandler = () => {
    if (!this.state.images.every((item) => item.image)) return;
    if (this.state.currentScale > 1) return;
    // if (Math.abs(x2 - x1) === 0) return;
    if (this.lastJumpFrame < 0.05) return;
    const velocity = this.lastJumpFrame;
    this.setState({ velocity: velocity });
    // Apply friction to slow down rotation
    const interval = setInterval(() => {
      // Update velocity with friction
      const updateVelocity = this.state.velocity - 0.17;
      this.setState({ velocity: updateVelocity });
      // Apply velocity to turntable rotation using CSS transform
      if (this.lastVelocity > 0) {
        this.handleTurnCar('right', this.state.velocity * 2);
      } else {
        this.handleTurnCar('left', this.state.velocity * 2);
      }
      // Stop interval when velocity reaches zero
      if (this.state.velocity < 0 || !this.state.velocity) {
        clearInterval(interval);
      }
      this.lastJumpFrame = 0;
    }, 16);
  };

  stopMoving = (e) => {
    this.transitionHandler(
      this.state.startX,
      this.state?.startTimestamp,
      e?.clientX,
      e?.timeStamp,
    );

    this.movement = false;
    this.movementStart = 0;

    // remove EventListener
    this.viewPortElementRef?.current?.removeEventListener(
      'mousemove',
      this.handleMouseMove,
    );
    this.viewPortElementRef?.current?.removeEventListener(
      'mouseup',
      this.stopMoving,
    );

    if (isMobile) return;
    this.viewPortElementRef.current.style.cursor =
      'url(/pointer.svg) 16 16, grab';
  };

  handleAction = (e) => {
    const carStatus = this.props.car.carStatus;

    if (carStatus === 'VDP') return;

    if (e.code === 'ArrowUp' && this.state.currentScale === 1) this.zoomIn();
    else if (e.code === 'ArrowDown' && this.state.currentScale > 1)
      this.zoomOut();
    else if (e.code === 'ArrowLeft') this.turnLeft();
    else if (e.code === 'ArrowRight') this.turnRight();
  };

  handleMouseMove = (evt) => {
    if (this.movement) {
      const { width, height } = this.props;
      const currentScale = this.state.currentScale;
      if (currentScale > 1) {
        const deltaX = evt.clientX - this.state.mouseX;
        const deltaY = evt.clientY - this.state.mouseY;

        let updatedX = evt.target.offsetLeft + deltaX;
        let updatedY = evt.target.offsetTop + deltaY;

        if (updatedX >= width / currentScale) updatedX = width / currentScale;
        else if (updatedX <= -width / currentScale)
          updatedX = -width / currentScale;

        if (updatedY >= height / currentScale) updatedY = height / currentScale;
        else if (updatedY <= -height / currentScale)
          updatedY = -height / currentScale;

        // Update the image position
        evt.target.style.left = `${updatedX}px`;
        evt.target.style.top = `${updatedY}px`;

        // Update mouse coordinates
        this.setState({
          mouseX: evt.clientX,
          mouseY: evt.clientY,
        });

        return;
      }

      this.onMove(evt.clientX, evt.timeStamp, evt?.target?.clientWidth);
    }
  };

  touchStart = (evt) => {
    this.movementStart = evt.touches[0].clientX;

    // Store initial position and mouse coordinates
    this.setState({
      mouseX: evt.touches[0].clientX,
      mouseY: evt.touches[0].clientY,
    });

    this.setState({
      startX: evt.touches[0].clientX,
      startTimestamp: evt.timeStamp,
    });

    // touch device
    this.viewPortElementRef?.current?.addEventListener(
      'touchmove',
      this.touchMove,
    );

    this.viewPortElementRef?.current?.addEventListener(
      'touchend',
      this.touchEnd,
    );

    this.generateLoadedItems();
  };

  touchMove = (evt) => {
    let portrait = window.matchMedia('(orientation: portrait)');

    const { width, height } = this.props;
    if (this.state.currentScale > 1) {
      const deltaX = evt.touches[0].clientX - this.state.mouseX;
      const deltaY = evt.touches[0].clientY - this.state.mouseY;

      let updatedX = evt.touches[0].target.offsetLeft + deltaX;
      let updatedY = evt.touches[0].target.offsetTop + deltaY;

      if (updatedX >= width / 2) updatedX = width / 2;
      else if (updatedX <= -width / 2) updatedX = -width / 2;

      if (updatedY >= height / 2) updatedY = height / 2;
      else if (updatedY <= -height / 2) updatedY = -height / 2;

      // Update the image position

      if (portrait.matches && window.innerWidth < 700) {
        evt.touches[0].target.style.top = '0px';
      } else {
        evt.touches[0].target.style.top = `${updatedY}px`;
      }

      evt.touches[0].target.style.left = `${updatedX}px`;

      // Update mouse coordinates
      this.setState({
        mouseX: evt.touches[0].clientX,
        mouseY: evt.touches[0].clientY,
      });

      return;
    }
    this.onMove(
      evt.touches[0].clientX,
      evt.timeStamp,
      evt?.target?.clientWidth,
    );
  };

  touchEnd = (e) => {
    this.transitionHandler(
      this.state.startX,
      this.state.startTimestamp,
      e?.changedTouches[0]?.clientX,
      e?.timeStamp,
    );
    this.movementStart = 0;

    // handel inertia after touch end.

    this.viewPortElementRef?.current?.removeEventListener(
      'touchmove',
      this.touchMove,
    );
    this.viewPortElementRef?.current?.removeEventListener(
      'touchend',
      this.stopDragging,
    );
  };

  play = () => {
    this.setState({
      loopTimeoutId: window.setInterval(() => {
        this.loopImages();
      }, 100),
    });
  };

  onSpin() {
    if (this.state.playing || this.state.loopTimeoutId) {
      this.stop();
    }
  }

  stop() {
    if (this.activeImage === 0) {
      this.setState({ currentLoop: 0 });
    }
    this.setState({ playing: false });
    window.clearTimeout(this.state.loopTimeoutId);
  }

  loopImages() {
    const loop = this.props.loop ? this.props.loop : 1;

    if (this.activeImage === 0) {
      if (this.state.currentLoop === loop) {
        this.stop();
      } else {
        this.setState({ currentLoop: this.state.currentLoop + 1 });

        this.turnRight();
      }
    } else {
      this.turnRight();
    }
  }

  togglePlay = () => {
    this.setState({ playing: !this.state.playing });
  };

  handleHeight = () => {
    if (this.state.currentScale > 1) {
      return '100%';
    } else {
      if (!this.state.ratio) return;
      const { width, height } = this.props;
      if (width / height > this.state.ratio) return width / this.state.ratio;
      else return height;
    }
  };

  handleWidth = () => {
    if (this.state.currentScale > 1) {
      return '100%';
    } else {
      if (!this.state.ratio) return;
      const { width, height } = this.props;
      if (width / height > this.state.ratio) return height * this.state.ratio;
      else return 'auto';
    }
  };

  handleClickHotspot = (evt, report = null, hotspotType = 'normal') => {
    const { clientX, clientY } = evt;

    this.props.closeHotSpotPositionSet({
      x: clientX + this.canvas?.current?.getBoundingClientRect()?.left,
      y: clientY + this.canvas?.current?.getBoundingClientRect()?.top,
    });
    this.props.carHotSpotCardStatusSet(2);
    this.props.carHotSpotCardOpenToggled();
    this.props.carHotSpotEnableSet(false);

    if (hotspotType === 'auto') {
      this.props.carHotSpotPositionsSet(report);
    } else {
      this.props.currentCarHotSpotIndexSet(report?.id);
      this.handleSetPositions();
    }
  };

  handleX = (data) => {
    const x = data?.x;
    return x * 100;
  };

  handleY = (data) => {
    const y = data?.y;
    return y * 100;
  };

  handleSetPositions = () => {
    const list = [];
    const { carModel } = this.props.car;

    const currentImage = carModel?.find(
      (car) => car?.order === this.state.currentImageOrder,
    );

    if (
      !currentImage?.reports ||
      currentImage?.order !== this.state.currentImageOrder
    ) {
      return;
    }

    currentImage?.reports?.map((report) => {
      if (Array.isArray(report)) {
        const newReportList = report?.map((item) => {
          return {
            ...item,
            x:
              (this.canvas?.current?.getBoundingClientRect()?.width / 100) *
                this.handleX(report) +
              this.canvas?.current?.getBoundingClientRect()?.left,
            y:
              (this.canvas?.current?.getBoundingClientRect()?.height / 100) *
                this.handleY(report) +
              this.canvas?.current?.getBoundingClientRect()?.top,
          };
        });

        newReportList.forEach((item) => {
          list.push(item);
        });
        return;
      }

      const value = {
        id: report?.id || null,
        title: report?.title || null,
        contentType: report?.contentType || null,
        filename: report?.filename || null,
        description: report?.description || null,
        order: this.state.currentImageOrder || null,
        x: 0,
        y: 0,
      };
      value.y =
        (this.canvas?.current?.getBoundingClientRect()?.height / 100) *
          this.handleY(report) +
        this.canvas?.current?.getBoundingClientRect()?.top;
      value.x =
        (this.canvas?.current?.getBoundingClientRect()?.width / 100) *
          this.handleX(report) +
        this.canvas?.current?.getBoundingClientRect()?.left;
      list.push(value);
    });

    const filteredList = list.filter(
      (report) => report.description !== process.env.REACT_APP_HOTSPOT_PAYLOAD,
    );

    this.props.carHotSpotPositionsSet(filteredList);
  };

  handleClickZoom = () => {
    if (this.state.currentScale === 1) {
      this.zoomIn();
      // handleDownload()
    } else {
      this.zoomOut();
    }
  };

  /**
   * download high quality from GCS
   */
  // handleDownload = () => {
  //   const userId = this.props.car.userId;
  //   const modelId = this.props.car.modelId;
  //   if (modelId && userId) {
  //     const currentImage = this.state.images.find(
  //       (image) => image.order === this.state.currentImageOrder,
  //     );

  //     const activeImage = this.activeImage;

  //     // if image once zoomed don't send request for new high quality image again
  //     if (currentImage?.zoom) return;
  //     const path = `qualities/zoom/${currentImage?.filename}`;

  //     handleDownloadFromGcs(userId, modelId, path).then((url) => {
  //       const image = new Image();
  //       image.src = url;
  //       image.onload = () => {
  //         this.updateSelectedItem(activeImage, image, true);
  //         this.update();
  //       };
  //     });
  //   }
  // };

  // change scale  by wheel
  handleWheel = (e) => {
    const carStatus = this.props.car.carStatus;
    if (carStatus === 'VDP') return;
    // Calculate the new scale based on the current scale and the wheel delta
    const newScale = Math.max(
      0.1,
      Math.min(10, this.state.currentScale + e.deltaY * -0.001),
    );
    this.zoom(newScale);
  };

  render() {
    const { currentScale, canvasLoaded } = this.state;
    const { width, height, mList, isPortrait } = this.props;
    const { carStatus, carHotSpotEnable, carModel, dragToExploreVisibility } =
      this.props.car;

    return (
      <div
        className="animate-fade-exterior"
        style={{
          position: carStatus === 'car' ? 'absolute' : 'initial',
          overflow: 'hidden',
          height: carStatus === 'car' ? height : '100%',
          width: carStatus === 'car' ? width : '100%',
        }}
      >
        {!canvasLoaded ? (
          <Box display={'flex'} justifyContent={'center'}>
            <Loading
              content={
                <p
                  ref={this.viewPercentageRef}
                  style={{
                    alignContent: 'center',
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  Loading...
                </p>
              }
            />
          </Box>
        ) : null}

        <V360
          container
          id="v360"
          onPointerDown={() => {
            if (this.state.images.length > 1) return;
            this.fetchData();
          }}
          page={carStatus}
          ref={this.viewPortElementRef}
          sx={{
            maxHeight: '100%',
            // prevents from overflow vdp image list on main 360 in landscape when aspectratio > screenratio
            ...(carStatus !== 'CAR' && { height: '100%' }),
            [theme.breakpoints.down('lg')]: {
              gap: carStatus === 'CAR' && isPortrait ? 6 : 0,
            },
          }}
        >
          {canvasLoaded && (
            <>
              <DragToExplore />
              <Buttons />
            </>
          )}

          <Grid
            item
            sx={{
              position: 'relative',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: 'max-content',
              height:
                width / height > this.state.ratio ? 'max-content' : '100%',
              maxHeight: '100%',
            }}
            xs={12}
          >
            <Canvas
              onMouseDown={this.handleMouseDown}
              onTouchStart={this.touchStart}
              ref={this.canvas}
              style={{
                display: canvasLoaded ? 'block' : 'none',
                transform: `scale(${currentScale})`,
                height: width / height > this.state.ratio ? '100%' : 'auto',
                width: this.handleWidth(),
                maxHeight: '100%',
                maxWidth: '100%',
                borderRadius: carStatus === 'VDP' && width > 900 ? '10px' : 0,
                [theme.breakpoints.down('lg')]: {
                  borderRadius: '0',
                },
                overflow: 'hidden',
                transition:
                  this.movement || this.movementStart
                    ? 'none'
                    : 'all 0.3s ease-in-out',
              }}
            />

            {!dragToExploreVisibility &&
              this.canvas?.current?.getBoundingClientRect()?.width > 0 &&
              this.state.canvasLoaded &&
              currentScale === 1 &&
              carHotSpotEnable && (
                <Box>
                  <HotspotButton
                    canvas={this.canvas?.current}
                    carModel={carModel}
                    currentImageOrder={this.state.currentImageOrder}
                    handleClickHotspot={this.handleClickHotspot}
                  />
                </Box>
              )}

            {!canvasLoaded && (
              <img
                alt="first image"
                src={this.state.firstImageURL}
                style={{
                  height: carStatus === 'VDP' ? '100%' : this.handleHeight(),
                  maxHeight: '100dvh',
                  aspectRatio: this.state.ratio,
                  borderRadius: '10px',
                  width: '100%',
                  overflow: 'hidden',
                  transition:
                    this.movement || this.movementStart
                      ? 'none'
                      : 'all 0.3s ease-in-out',
                }}
              />
            )}
          </Grid>

          {canvasLoaded &&
            Boolean(mList) &&
            carStatus === 'VDP' &&
            this.props.isMobile &&
            canvasLoaded &&
            height > width &&
            mList}

          {canvasLoaded && viewPage !== 'admin' && (
            <ExteriorButtonContainer
              currentScale={currentScale}
              disableZoomButton={!this.state.images.every((item) => item.image)}
              handleClickZoom={this.handleClickZoom}
            />
          )}
        </V360>
      </div>
    );
  }
}

function mapStateToProps(state) {
  const car = state.car;
  return {
    car,
  };
}

const mapDispatchToProps = (dispatch) => ({
  carHotSpotCardOpenToggled: (data) =>
    dispatch(carHotSpotCardOpenToggled(data)),
  carHotSpotCardStatusSet: (data) => dispatch(carHotSpotCardStatusSet(data)),
  carHotSpotEnableSet: (data) => dispatch(carHotSpotEnableSet(data)),
  carHotSpotPositionsSet: (data) => dispatch(carHotSpotPositionsSet(data)),
  closeHotSpotPositionSet: (data) => dispatch(closeHotSpotPositionSet(data)),
  car360LoadedSet: (data) => dispatch(car360LoadedSet(data)),
  dragToExploreVisibilitySet: (data) =>
    dispatch(dragToExploreVisibilitySet(data)),
  rotate360VisibilitySet: (data) => dispatch(rotate360VisibilitySet(data)),
  exteriorZoomScaleSet: (data) => dispatch(exteriorZoomScaleSet(data)),
  car360LoadedSet: (data) => dispatch(car360LoadedSet(data)),
  dragToExploreVisibilitySet: (data) =>
    dispatch(dragToExploreVisibilitySet(data)),
  currentCarHotSpotIndexSet: (index) =>
    dispatch(currentCarHotSpotIndexSet(index)),
  carImageRatioSet: (index) => dispatch(carImageRatioSet(index)),
});

export default connect(mapStateToProps, mapDispatchToProps)(React360Viewer);
